import React, { useRef } from 'react';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createProductBatch,
  getProductBatches,
  getSingleProductBatch,
  updateProductBatch,
  deleteProductBatch,
} from '../../redux/features/product/productBatchSlice';
import { getProducts } from '../../redux/features/product/productSlice';
import { toast } from 'react-toastify';
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { useRedirectLogOutUser } from "../../customHook/useRedirectLogOutUser";
import { useRedirectEmployee } from "../../customHook/useRedirectEmploye";
import { MdEditSquare, MdDeleteForever } from 'react-icons/md';
import { AiOutlinePlus } from "react-icons/ai";
import ButtonLoading from '../../loader/ButtonLoader';
import ReactToPrint from 'react-to-print';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { Modal } from "bootstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ColumnToggle } from "./ColumnToggle";
import { calcBuyOptions } from '../../utils/ceramicCalculator';
import { getBoxInfo } from '../../utils/boxInfo';
import { EthiopianDateCell } from './EthiopianDateCell';
import { SimpleDualDateInput } from '../DualDateInput';


// ─── helpers ─────────────────────────────────────────────────
const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;
const formatDate = (d) => d
  ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
  : '';

const initialState = {
  product:           null,  // { value: id, label: name }
  quantity:          "",
  purchasePrice:     "",
  sellingPrice:      "",
  minSellingPrice:   "",
  maxSellingPrice:   "",
  remainingQuantity: "",
  supplier:          "",
  reference:         "",
  purchaseDate:      "",
};

export const ProductBatch = () => {
  useRedirectLogOutUser();
  useRedirectEmployee();

  const dispatch     = useDispatch();
  const componentRef = useRef();

  const addModalRef    = useRef(null);
  const editModalRef   = useRef(null);
  const deleteModalRef = useRef(null);

  const [productBatchForm, setproductBatchForm]   = useState(initialState);
  const [isLoading, setIsLoading]   = useState(false);
  const [printView, setPrintView]   = useState(false);
  const [editId, setEditId]         = useState(null);
  const [deleteId, setDeleteId]     = useState(null);

  const { productBatches, productBatch }  = useSelector((state) => state.productBatch);
  const { products }        = useSelector((state) => state.product);
  const { userType }        = useSelector((state) => state.auth);
  const isAdmin             = userType === "admin";

  console.log("productBatches:", productBatches);

  // ─── Product options for react-select ────────────────────────
  const productOptions = useMemo(() =>
    products.map(p => ({ value: p._id, label: `${p.name} (${p.sku} (${p.category.measurementUnit}))` })),
  [products]);

  // ─── Fetch on mount ──────────────────────────────────────────
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    dispatch(getProductBatches());
    dispatch(getProducts());
  }, [dispatch]);

  // ─── Populate edit form ──────────────────────────────────────
  useEffect(() => {
    if (productBatch && editId) {
      setproductBatchForm({
        product: productBatch.product
          ? { value: productBatch.product._id, label: `${productBatch.product.name} (${productBatch.product.sku})` }
          : null,
        quantity:          productBatch.quantity          ?? "",
        purchasePrice:     productBatch.purchasePrice     ?? "",
        sellingPrice:      productBatch.sellingPrice      ?? "",
        minSellingPrice:   productBatch.minSellingPrice   ?? "",
        maxSellingPrice:   productBatch.maxSellingPrice   ?? "",
        remainingQuantity: productBatch.remainingQuantity ?? "",
        supplier:          productBatch.supplier          ?? "",
        reference:         productBatch.reference         ?? "",
        purchaseDate:      productBatch.purchaseDate
          ? new Date(productBatch.purchaseDate).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
    }
  }, [productBatch, editId]);

  // ─── Auto-calculate totalCost ─────────────────────────────────
  const totalCost = useMemo(() => {
    const qty   = Number(productBatchForm.quantity)      || 0;
    const price = Number(productBatchForm.purchasePrice) || 0;
    return (qty * price).toFixed(2);
  }, [productBatchForm.quantity, productBatchForm.purchasePrice]);

  // ── Selected product doc ───────────────────────────────────────
const selectedProductDoc = useMemo(() =>
  products.find(p => p._id === productBatchForm.product?.value),
[products, productBatchForm.product]);

const batchUnit    = selectedProductDoc?.category?.measurementUnit || "pcs";
const isBoxedBatch = !!(selectedProductDoc?.itemsPerBox);
const hasSizeBatch = !!(selectedProductDoc?.sizePerItem);
const showBatchCalc = isBoxedBatch || hasSizeBatch;

// ── Buy options for the entered quantity ──────────────────────
// Same logic as sales — recommends nearest valid sellable quantity
const batchBuyOptions = useMemo(() => {
  if (!showBatchCalc) return null;
  const qty = Number(productBatchForm.quantity);
  if (!qty || qty <= 0) return null;
  return calcBuyOptions(qty, selectedProductDoc, batchUnit);
}, [productBatchForm.quantity, selectedProductDoc, batchUnit, showBatchCalc]);

// ── Box info for the entered quantity ─────────────────────────
const batchBoxInfo = useMemo(() => {
  if (!isBoxedBatch || !productBatchForm.quantity) return null;
  return getBoxInfo(Number(productBatchForm.quantity), selectedProductDoc);
}, [productBatchForm.quantity, selectedProductDoc, isBoxedBatch]);

  // ─── Modal openers ───────────────────────────────────────────
  const openAddModal = () => {
    setproductBatchForm(initialState);
    document.activeElement?.blur();
    new Modal(addModalRef.current).show();
  };

  const openEditModal = (id) => {
    setEditId(id);
    dispatch(getSingleProductBatch(id));
    document.activeElement?.blur();
    new Modal(editModalRef.current).show();
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    document.activeElement?.blur();
    new Modal(deleteModalRef.current).show();
  };

  // ─── Input handler ────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setproductBatchForm(prev => ({ ...prev, [name]: value }));
  };

  // ─── Add submit — direct, keep modal open ────────────────────
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const { product, quantity, purchasePrice, sellingPrice, minSellingPrice, maxSellingPrice } = productBatchForm;
    if (!product || !quantity || !purchasePrice || !minSellingPrice || !maxSellingPrice) {
      return toast.error("Please fill in all required fields");
    }
    setIsLoading(true);
    await dispatch(createProductBatch({
      product:           product.value,
      quantity:          Number(quantity),
      purchasePrice:     Number(purchasePrice),
      sellingPrice:      Number(sellingPrice),
      minSellingPrice:   Number(minSellingPrice),
      maxSellingPrice:   Number(maxSellingPrice),
      remainingQuantity: Number(quantity), // starts equal to quantity
      totalCost,
      supplier:          productBatchForm.supplier    || null,
      reference:         productBatchForm.reference   || null,
      purchaseDate:      productBatchForm.purchaseDate || new Date().toISOString().split("T")[0],
    }));
    setIsLoading(false);
    // ✅ Reset form but keep modal open — user adds next productBatch
    setproductBatchForm(initialState);
    dispatch(getProductBatches());
    toast.success("Batch saved — add another or close the modal.");
  };

  // ─── Edit submit ──────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    await dispatch(updateProductBatch({
      id: editId,
      data: {
        product:           productBatchForm.product?.value,
        quantity:          Number(productBatchForm.quantity),
        purchasePrice:     Number(productBatchForm.purchasePrice),
        sellingPrice:      Number(productBatchForm.sellingPrice),
        minSellingPrice:   Number(productBatchForm.minSellingPrice),
        maxSellingPrice:   Number(productBatchForm.maxSellingPrice),
        remainingQuantity: Number(productBatchForm.remainingQuantity),
        totalCost,
        supplier:          productBatchForm.supplier  || null,
        reference:         productBatchForm.reference || null,
        purchaseDate:      productBatchForm.purchaseDate || new Date().toISOString().split("T")[0],
      }
    }));
    dispatch(getProductBatches());
    Modal.getInstance(editModalRef.current).hide();
  };

  // ─── Delete ───────────────────────────────────────────────────
  const handleDelete = () => {
    dispatch(deleteProductBatch(deleteId));
    dispatch(getProductBatches());
    Modal.getInstance(deleteModalRef.current).hide();
  };

  const handlePrint = () => setPrintView(true);
  const handleBeforeGetContent = async () => await setPrintView(true);

  // ─── Shared form fields ───────────────────────────────────────
  const renderFormFields = (isEdit = false) => (
    <div className="row g-3">

      {/* Product — react-select */}
      <div className="col-12">
        <label className="fs-4">Product</label>
        <Select
          options={productOptions}
          value={productBatchForm.product}
          onChange={(selected) =>
            setproductBatchForm(prev => ({ ...prev, product: selected || null }))
          }
          placeholder="Search and select product..."
          isClearable
          isSearchable
          classNamePrefix="react-select"
          styles={{
            control: (base) => ({ ...base, minHeight: "50px", fontSize: "1.5rem" }),
            menu:    (base) => ({ ...base, zIndex: 9999, fontSize: "1.5rem" }),
          }}
        />
      </div>

      {/* Quantity + Purchase Price — side by side */}
      {/* Quantity */}
      <div className="col-13">
        <label className="h4">
          Quantity
          <span className="text-muted ms-1 fs-5">
            ({batchUnit === "m2" ? "m²" : batchUnit === "linear_meter" ? "m" : "pcs"})
          </span>
        </label>
        <input
          className="form-control form-control-lg border-2"
          type="number" min="0.001" step="0.001"
          placeholder="0"
          name="quantity"
          value={productBatchForm.quantity}
          onChange={handleInputChange}
        />

        {/* ── Quantity calculator — same pattern as sales ── */}
        {showBatchCalc && productBatchForm.quantity && (
          <div className="mt-2">

            {/* Options — click to snap to valid sellable quantity */}
            {batchBuyOptions && (
              <div className="d-flex flex-wrap gap-2 mt-1 overflow-auto">

                {/* Round up — recommended */}
                <button type="button"
                  className={`btn btn-sm rounded-3 fs-5 ${
                    Number(productBatchForm.quantity) === batchBuyOptions.up.quantityValue
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() =>
                    setproductBatchForm(prev => ({
                      ...prev, quantity: batchBuyOptions.up.quantityValue
                    }))
                  }>
                  <span className="badge bg-success me-1 fs-5">Recommended</span>
                  ▲ {batchBuyOptions.up.sizeLabel} — {batchBuyOptions.up.pieces} pcs
                  {batchBuyOptions.up.boxLabel && (
                    <span className="ms-1 opacity-75">
                      ({batchBuyOptions.up.boxLabel})
                    </span>
                  )}
                </button>

                {/* Round down — only shown when different from up */}
                {batchBuyOptions.down && (
                  <button type="button"
                    className={`btn btn-sm rounded-3 fs-5 ${
                      Number(productBatchForm.quantity) === batchBuyOptions.down.quantityValue
                        ? "btn-warning text-dark"
                        : "btn-outline-warning text-dark"
                    }`}
                    onClick={() =>
                      setproductBatchForm(prev => ({
                        ...prev, quantity: batchBuyOptions.down.quantityValue
                      }))
                    }>
                    ▼ {batchBuyOptions.down.sizeLabel} — {batchBuyOptions.down.pieces} pcs
                    {batchBuyOptions.down.boxLabel && (
                      <span className="ms-1 opacity-75">
                        ({batchBuyOptions.down.boxLabel})
                      </span>
                    )}
                  </button>
                )}

              </div>
            )}

            {/* Box info summary for the entered/selected quantity */}
            {batchBoxInfo && (
              <div className="d-flex flex-wrap justify-content-center gap-2 mt-2 text-center">
                <span className="badge bg-primary bg-opacity-25 text-primary
                                rounded-pill px-3 py-1 fs-5">
                  {batchBoxInfo.boxLabel}
                </span>
                <span className="badge bg-info bg-opacity-25 text-info
                                rounded-pill px-3 py-1 fs-5">
                  {batchBoxInfo.sizeLabel}
                </span>
                {batchUnit !== "pcs" && (
                  <span className="badge bg-secondary bg-opacity-25 text-secondary
                                  rounded-pill px-3 py-1 fs-5">
                    {batchBoxInfo.totalPcs} pcs total
                  </span>
                )}
              </div>
            )}

          </div>
        )}
      </div>

      <div className="col-6">
        <label className="h4">Purchase Price</label>
        <input
          className="form-control form-control-lg border-2"
          type="number" min="0"
          placeholder="0.00"
          name="purchasePrice"
          value={productBatchForm.purchasePrice}
          onChange={handleInputChange}
        />
      </div>

      {/* Total Cost — auto-calculated, read only */}
      <div className="col-6">
        <label className="h4">Total Cost</label>
        <input
          className="form-control form-control-lg border-2 bg-white"
          type="number"
          value={totalCost}
          readOnly
          tabIndex={-1}
          style={{ color: "#198754", fontWeight: 600 }}
        />
      </div>

      {/* Selling Price */}
        <div className="col-6">
          <label className="h4">
            Min Selling Price <span className="text-danger">*</span>
          </label>
          <input
            className="form-control form-control-lg border-2"
            type="number" min="0"
            placeholder="0.00"
            name="minSellingPrice"
            value={productBatchForm.minSellingPrice}
            onChange={handleInputChange}
          />
          <div className="form-text fs-5">Lowest acceptable price</div>
        </div>

        <div className="col-6">
          <label className="h4">
            Max Selling Price <span className="text-danger">*</span>
          </label>
          <input
            className="form-control form-control-lg border-2"
            type="number" min="0"
            placeholder="0.00"
            name="maxSellingPrice"
            value={productBatchForm.maxSellingPrice}
            onChange={handleInputChange}
          />
          <div className="form-text fs-5">Standard / maximum price</div>
        </div>

      {/* Remaining Quantity — only in edit */}
      {isEdit && (
        <div className="col-6">
          <label className="h4">Remaining Quantity</label>
          <input
            className="form-control form-control-lg border-2"
            type="float" min="0"
            placeholder="0"
            name="remainingQuantity"
            value={productBatchForm.remainingQuantity}
            onChange={handleInputChange}
          />
        </div>
      )}

      {/* Supplier + Reference — side by side */}
      <div className="col-6">
        <label className="h4">Supplier</label>
        <input
          className="form-control form-control-lg border-2"
          type="text"
          placeholder="Supplier name (optional)"
          name="supplier"
          value={productBatchForm.supplier}
          onChange={handleInputChange}
        />
      </div>

      {/* Purchase Date */}
      <div className="col-6">
        <SimpleDualDateInput
          label="Purchase Date"
          value={productBatchForm.purchaseDate}
          onChange={(date) => setproductBatchForm(prev => ({ ...prev, purchaseDate: date }))}
          maxDate={new Date()}
        />
      </div>


      <div className="col-6">
        <label className="h4">Reference</label>
        <input
          className="form-control form-control-lg border-2"
          type="text"
          placeholder="Invoice / ref no. (optional)"
          name="reference"
          value={productBatchForm.reference}
          onChange={handleInputChange}
        />
      </div>

      
    </div>
  );

  const attributeNames = useMemo(() => {
  const set = new Set();

  productBatches.forEach(batch => {
    batch.product?.attributes?.forEach(a => {
      if (a.attribute?.name) {
        set.add(a.attribute.name);
      }
    });
  });

  return [...set];
}, [productBatches]);


const normalizedData = useMemo(() => {
  return productBatches.map(batch => {
    const attrMap = {};

    batch.product?.attributes?.forEach(a => {
      if (a.attribute?.name) {
        attrMap[a.attribute.name] = a.value;
      }
    });

    return {
      ...batch,
      productName: batch.product?.name,
      sku: batch.product?.sku,
      category: batch.product?.category?.name,
      ...attrMap // 👈 THIS IS THE MAGIC
    };
  });
}, [productBatches]);

const attributeColumns = useMemo(() => {
  return attributeNames.map(attrName => ({
    Header: attrName,
    accessor: attrName,

    Filter: SelectColumnFilter,
    filter: 'multiSelectFilter',

    Cell: ({ value }) =>
      value ? (
        <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-2 py-1 fs-6">
          {value}
        </span>
      ) : (
        <span className="text-muted">—</span>
      )
  }));
}, [attributeNames]);

  // ─── Table columns ────────────────────────────────────────────
  const columns = useMemo(() => {
    const baseColumns = [
      {
        Header: 'No',
        accessor: 'no',
        Cell: ({ row }) => row.index + 1
      },
      {
        Header: 'Product',
        accessor: 'product.name',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'SKU',
        accessor: 'product.sku',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Category',
        accessor: 'product.category.name',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      {
      Header: 'Attributes',
      columns: attributeColumns
    },
      {
        Header: 'Purchase Qty',
        accessor: 'quantity',
        Cell: ({ value }) => dash(value)
      },
      
      {
        Header: 'Remaining qty',
        accessor: 'remainingQuantity',
        Cell: ({ value, row }) => {
          const qty       = row.original.quantity || 1;
          const remaining = value || 0;
          const pct       = Math.round((remaining / qty) * 100);
          const color     = pct > 50 ? 'success' : pct > 20 ? 'warning' : 'danger';
          return (
            <span className={`badge bg-${color} bg-opacity-15 text-${color} rounded-pill px-2 py-1`}>
              <p className='mb-0'>{value.toFixed(2)}</p>
            </span>
          );
        }
      },
      // Replace the empty 'Box of ceramic' column with:
      {
        Header: 'Box Info',
        id: 'boxInfo',
        Cell: ({ row }) => {
          const p    = row.original.product;
          const rem  = row.original.remainingQuantity || 0;
          if (!p?.itemsPerBox) return <span className="text-muted">—</span>;

          const info = getBoxInfo(rem, p);

          return (
            <div className="d-flex flex-column gap-1">
              <span className="badge bg-primary bg-opacity-25 text-primary rounded-pill px-2 py-1 fs-4">
                {info.boxLabel}
              </span>
                <span className="badge bg-info bg-opacity-25 text-info rounded-pill px-2 py-1 fs-4">
                  {info.sizeLabel}
                </span>
                { !info.unitLabel.includes("pcs") &&
                  <span className="badge bg-secondary bg-opacity-25 text-secondary rounded-pill px-2 py-1 fs-4">
                    {info.totalPcs} pcs total
                  </span>
                }
            </div>
          );
        }
      },
      {
        Header: 'Measurement Unit',
        accessor: 'product.category.measurementUnit',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Purchase Price',
        accessor: 'purchasePrice',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Min Price',
        accessor: 'minSellingPrice',
        Cell: ({ value, row }) => {
          // ✅ fall back to old sellingPrice for unmigrated docs
          const price = value || row.original.sellingPrice;
          return price != null ? price.toLocaleString() : "—";
        }
      },
      {
        Header: 'Max Price',
        accessor: 'maxSellingPrice',
        Cell: ({ value, row }) => {
          const price = value || row.original.sellingPrice;
          return price != null ? price.toLocaleString() : "—";
        }
      },
      {
        Header: 'Total Cost',
        accessor: 'totalCost',
        Cell: ({ value }) => value != null
          ? <span className="fw-semibold text-success">{value.toLocaleString()}</span>
          : "—"
      },
      {
        Header: 'Supplier',
        accessor: 'supplier',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Reference',
        accessor: 'reference',
        Cell: ({ value }) => dash(value)
      },
      {
        Header: 'Purchase Date',
        accessor: 'purchaseDate',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => value ? <EthiopianDateCell value={value} dual /> : "—"
      },
      {
        Header: 'Added By',
        accessor: 'addedBy.firstname',
        Filter: SelectColumnFilter,
        filter: 'multiSelectFilter',
        Cell: ({ value }) => dash(value)
      },
    ];

    if (!printView && isAdmin) {
      baseColumns.push({
        Header: 'Action',
        Cell: ({ row }) => (
          <>
            <span className="text-warning" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openEditModal(row.original._id)}>
              <MdEditSquare size={24} />
            </span>
            <span className="text-danger" style={{ padding: '5px', cursor: 'pointer' }}
              onClick={() => openDeleteModal(row.original._id)}>
              <MdDeleteForever size={24} />
            </span>
          </>
        )
      });
    }

    return baseColumns;
  }, [printView, isAdmin, attributeColumns]);

  const data        = useMemo(() => normalizedData || [], [normalizedData]);
  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns, 
    data,
    initialState: { hiddenColumns: ['_id', 'reference', 'supplier', 'Size', 'Boxed', 'one box quantity', 'Isprinting'] }, // hide these by default
    filterTypes,
    defaultColumn: { Filter: () => null }
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow, allColumns, getToggleHideAllColumnsProps
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  return (
    <div>

      {/* ── ADD MODAL ─────────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={addModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <AiOutlinePlus color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Add Product Batch</h3>
                  <small className="text-muted fs-5">
                    Batch saves immediately — modal stays open to add more.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form onSubmit={handleAddSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(false)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal">
                  Close
                </button>
                {isLoading ? (
                  <ButtonLoading
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2"
                    disabled>
                    Saving...
                  </ButtonLoading>
                ) : (
                  <button type="submit"
                    className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                    Save Batch
                  </button>
                )}
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── EDIT MODAL ────────────────────────────────────────── */}
      <div className="modal fade modal-dialog-scrollable" ref={editModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg ">
          <div className="modal-content border-0 rounded-4 shadow-lg p-2">

            <div className="modal-header pb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#e7f1ff" }}>
                  <MdEditSquare color="#0d6efd" size={28} />
                </div>
                <div>
                  <h3 className="modal-title fw-semibold mb-1">Edit Batch</h3>
                  <small className="text-muted fs-5">
                    Update batch details and save changes.
                  </small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <form onSubmit={handleEditSubmit} className="border-0">
              <div className="modal-body py-3">
                <div className="bg-light rounded-3 px-3 py-3">
                  {renderFormFields(true)}
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button type="button"
                  className="btn btn-danger border flex-fill rounded-3 fw-medium py-2"
                  data-bs-dismiss="modal">Cancel</button>
                <button type="submit"
                  className="btn btn-primary flex-fill rounded-3 fw-medium py-2">
                  Update Batch
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>

      {/* ── DELETE MODAL ──────────────────────────────────────── */}
      <div className="modal fade" ref={deleteModalRef} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 rounded-4 shadow-lg p-3">

            <div className="modal-header border-0 pb-0">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 48, height: 48, backgroundColor: "#fde2e2" }}>
                  <MdDeleteForever color="red" size={34} />
                </div>
                <div>
                  <h5 className="modal-title fw-semibold mb-0">Delete Batch</h5>
                  <small className="text-muted">This action cannot be undone.</small>
                </div>
              </div>
              <button className="btn-close ms-auto" data-bs-dismiss="modal" />
            </div>

            <div className="modal-body py-3">
              <div className="bg-light rounded-3 px-3 py-3">
                <p className="mb-0">
                  Are you sure you want to delete this batch? Stock levels
                  will not be automatically adjusted.
                </p>
              </div>
            </div>

            <div className="modal-footer border-0 pt-0 gap-2">
              <button className="btn btn-primary border flex-fill rounded-3 fw-medium py-2"
                data-bs-dismiss="modal">Cancel</button>
              <button className="btn btn-danger flex-fill rounded-3 fw-medium py-2"
                onClick={handleDelete}>
                <MdDeleteForever size={18} className="me-1" />Delete
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ── TABLE ─────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center m-4 px-4 py-2 rounded-4 border"
        style={{ backgroundColor: "#faf8f5" }}>
        <h4 className="mb-0 fs-2">Product Batches</h4>
        <div className="d-flex gap-2">
          <ReactToPrint
            trigger={() => (
              <button className="btn btn-primary rounded-3" onClick={handlePrint}>
                Print Table
              </button>
            )}
            content={() => componentRef.current}
            onAfterPrint={() => setPrintView(false)}
            onBeforeGetContent={handleBeforeGetContent}
          />
          <button className="btn btn-primary rounded-3" onClick={openAddModal}>
            <AiOutlinePlus className="me-1" /> Add Stock
          </button>
        </div>
      </div>

      <div className="px-4 mb-4">
        <div className="d-flex align-items-center justify-content-between gap-3 px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
          <ColumnToggle allColumns={allColumns} getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}/>
        </div>
      </div>

      <div className="px-4">
        <div style={{ margin: printView ? "30px" : null }} ref={componentRef}>
          {printView && (
            <>
              <div style={{ padding: "1.5rem" }}><h2>Product Batches</h2></div>
              <div style={{ marginBottom: '1rem', textAlign: "right", fontSize: "1.5rem" }}>
                Date: {new Date().toLocaleDateString()}
              </div>
            </>
          )}
          <div className="table-responsive rounded-4 border mt-5"
            style={{ backgroundColor: "#ffffff" }}>
            <table className="table table-hover table-striped caption-top fs-4"
              {...getTableProps()}
              style={{ minWidth: "100%", fontSize: "12px", margin: 0 }}>
              <thead>
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th className="px-4"
                        style={{ backgroundColor: "#faf8f5" }}
                        {...column.getHeaderProps()}>
                        {column.render('Header')}
                        {!printView && column.canFilter && column.render('Filter')}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map(row => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map(cell => (
                        <td className="px-4"
                          style={{ backgroundColor: "#ffffff" }}
                          {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {page.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="text-center text-muted py-4">
                      No batches found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: '15px' }}>
            page{' '}
            <strong>
              <em style={{ color: 'blue' }}>{pageIndex + 1}</em> of {pageOptions.length}
            </strong>
          </span>
          <select className="form-select form-select-lg" style={{ maxWidth: '90px' }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 15, 20, 30, 40, 50, data.length].map((size, i) => (
              <option key={i} value={size}>Show {size}</option>
            ))}
          </select>
          <li>
            <button className="page-link" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>
          </li>
          <li className={`page-item ${!canPreviousPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={previousPage} disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? 'disabled' : ''}`}>
            <button className="page-link" onClick={nextPage} disabled={!canNextPage}>Next</button>
          </li>
          <li>
            <button className="page-link" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>
          </li>
        </ul>
      </div>

    </div>
  );
};